import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import api, { initApiAuth, refreshAccessToken } from '../lib/api'
import { queryClient } from '../lib/queryClient'
import i18n from '../i18n'
import type { Locale } from '../i18n/locales'
import { updateUserLanguage } from '../api/auth'
import { getModuleById, isModulePublic } from '../config/moduleRegistry'
import type { MarketingAttribution } from '../services/attribution'

const PERMISSION_THROTTLE_MS = 30_000
const AUTH_STORAGE_KEY = 'auth-storage'

/** Drop legacy zustand persist blobs that stored access/refresh tokens in localStorage. */
function clearLegacyAuthStorageWithTokens(): void {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as { state?: { token?: unknown; refreshToken?: unknown } }
    if (parsed?.state?.token != null || parsed?.state?.refreshToken != null) {
      localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY)
  }
}

clearLegacyAuthStorageWithTokens()

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_verified: boolean
  role: 'user' | 'admin'
  company_domain?: string
  google_id?: string
  created_at: string
  accessible_accounts: number[]
  permissions?: Record<string, boolean>
  requires_document_acceptance?: boolean
  missing_documents?: string[]
  language?: string
  phone_number?: string
  requires_marketing_prompt?: boolean
  marketing_consent_state?: Record<string, boolean>
  marketing_consent_version?: string
}

/** Login / verify / refresh JSON body — access token only; refresh is httpOnly cookie. */
export interface AuthTokenResponse {
  access_token: string
  token_type?: string
  user?: User
}

export interface AuthState {
  user: User | null
  /** In-memory access token only — never persisted. */
  token: string | null
  isAuthenticated: boolean
  /** False until cookie refresh bootstrap finishes (success or no-session). */
  isSessionReady: boolean
  isLoading: boolean
  permissions: Record<string, boolean>
  showDocumentModal: boolean
  documentModalDismissed: boolean
  showMarketingModal: boolean
  permissionsLoading: boolean
  permissionsLastFetchedAt: number
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  googleLogin: (token: string) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => void
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, password: string) => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  resendVerification: (email: string) => Promise<void>
  updateLanguage: (language: Locale) => Promise<void>
  refreshAuth: () => Promise<void>
  /** App boot: mint access token from refresh cookie (if any). */
  bootstrapSession: () => Promise<void>
  setLoading: (loading: boolean) => void
  setAccessToken: (accessToken: string, user?: User) => void
  canAccessAccount: (accountId: number) => boolean
  getUserRole: () => string
  getCurrentUser: () => Promise<void>
  hasPermission: (moduleName: string) => boolean
  hasManualModuleGrant: (moduleName: string) => boolean
  loadUserPermissions: (force?: boolean) => Promise<void>
  updatePermissions: (permissions: Record<string, boolean>) => void
  initializePermissions: () => Promise<void>
  checkDocumentRequirements: () => void
  dismissDocumentModal: () => void
  documentAccepted: () => void
  dismissMarketingModal: () => void
  marketingConsentSaved: (state: Record<string, boolean>) => void
}

export interface DocumentAcceptanceData {
  document_type: string
  document_version: string
}

export interface RegisterData {
  email: string
  password: string
  first_name: string
  last_name: string
  language?: string
  phone_number?: string
  accepted_documents?: DocumentAcceptanceData[]
  marketing_consents?: Record<string, boolean>
  referral_code?: string
  attribution?: MarketingAttribution
  heard_about: string
  heard_about_detail?: string
}

type AuthStore = AuthState & AuthActions

let bootstrapPromise: Promise<void> | null = null

function applyAuthSuccess(
  set: (partial: Partial<AuthStore>) => void,
  data: AuthTokenResponse,
): void {
  const { access_token, user } = data
  set({
    token: access_token,
    isAuthenticated: true,
    ...(user ? { user } : {}),
  })
}

// Note: Authentication interceptors are configured in lib/api.ts

export const useAuthStore = create<AuthStore>()((set, get) => ({
  // State
  user: null,
  token: null,
  isAuthenticated: false,
  isSessionReady: false,
  isLoading: false,
  permissions: {},
  showDocumentModal: false,
  documentModalDismissed: false,
  showMarketingModal: false,
  permissionsLoading: false,
  permissionsLastFetchedAt: 0,

  // Actions
  setLoading: (loading: boolean) => set({ isLoading: loading }),

  setAccessToken: (accessToken: string, user?: User) => {
    set({
      token: accessToken,
      isAuthenticated: true,
      ...(user ? { user } : {}),
    })
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const response = await api.post<AuthTokenResponse>('/auth/login', {
        email,
        password,
      })

      applyAuthSuccess(set, response.data)
      set({ isLoading: false, isSessionReady: true })

      const { getCurrentUser, loadUserPermissions } = get()
      await getCurrentUser()
      await loadUserPermissions(true)
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  googleLogin: async (token: string) => {
    set({ isLoading: true })
    try {
      const response = await api.post<AuthTokenResponse>('/auth/google-login', {
        token,
      })

      applyAuthSuccess(set, response.data)
      set({ isLoading: false, isSessionReady: true })

      const { getCurrentUser, loadUserPermissions } = get()
      await getCurrentUser()
      await loadUserPermissions(true)
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  register: async (userData: RegisterData) => {
    set({ isLoading: true })
    try {
      await api.post('/auth/register', userData)
      set({ isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  logout: () => {
    try {
      window.Quackback?.('logout')
    } catch {
      /* Quackback may be unloaded */
    }
    Sentry.setUser(null)
    queryClient.clear()
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isSessionReady: true,
      isLoading: false,
      permissions: {},
      showDocumentModal: false,
      documentModalDismissed: false,
      showMarketingModal: false,
    })
  },

  forgotPassword: async (email: string) => {
    set({ isLoading: true })
    try {
      await api.post('/auth/forgot-password', { email })
      set({ isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  resetPassword: async (token: string, new_password: string) => {
    set({ isLoading: true })
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password,
      })
      set({ isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  verifyEmail: async (token: string) => {
    set({ isLoading: true })
    try {
      const response = await api.post<AuthTokenResponse>(`/auth/verify-email/${token}`)

      applyAuthSuccess(set, response.data)
      set({ isLoading: false, isSessionReady: true })

      const { getCurrentUser, loadUserPermissions } = get()
      await getCurrentUser()
      await loadUserPermissions(true)
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  resendVerification: async (email: string) => {
    set({ isLoading: true })
    try {
      await api.post('/auth/resend-verification', { email })
      set({ isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  updateLanguage: async (language: Locale) => {
    const previousLanguage = i18n.language
    const previousUserLanguage = get().user?.language

    // Optimistic update
    i18n.changeLanguage(language)
    localStorage.setItem('i18nextLng', language)

    try {
      const result = await updateUserLanguage(language)
      if (get().user) {
        set((s) => (s.user ? { user: { ...s.user, language: result.language } } : {}))
      }
    } catch (error) {
      // Revert on failure
      i18n.changeLanguage(previousLanguage)
      if (previousLanguage) localStorage.setItem('i18nextLng', previousLanguage)
      if (get().user && previousUserLanguage !== undefined) {
        set((s) =>
          s.user ? { user: { ...s.user, language: previousUserLanguage } } : {},
        )
      }
      throw error
    }
  },

  refreshAuth: async () => {
    try {
      await refreshAccessToken()
      const { user, getCurrentUser, loadUserPermissions } = get()
      if (!user) {
        await getCurrentUser()
      }
      await loadUserPermissions(true)
    } catch (error) {
      set({
        user: null,
        token: null,
        isAuthenticated: false,
      })
      throw error
    }
  },

  bootstrapSession: async () => {
    if (get().isSessionReady) return
    if (bootstrapPromise) return bootstrapPromise

    bootstrapPromise = (async () => {
      try {
        await refreshAccessToken()
        const { getCurrentUser, loadUserPermissions } = get()
        // refresh response may already include user via setAccessToken
        if (!get().user) {
          await getCurrentUser()
        } else {
          get().checkDocumentRequirements()
        }
        await loadUserPermissions(true)
      } catch {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })
      } finally {
        set({ isSessionReady: true })
        bootstrapPromise = null
      }
    })()

    return bootstrapPromise
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me')
      const userData = response.data
      set({ user: userData })
      Sentry.setUser({
        id: String(userData.id),
        email: userData.email,
        username: `${userData.first_name} ${userData.last_name}`.trim(),
      })
      if (userData.language) {
        Sentry.setTag('language', userData.language)
      }

      const { checkDocumentRequirements } = get()
      checkDocumentRequirements()
    } catch (error) {
      console.error('Nie udało się pobrać danych użytkownika:', error)
    }
  },

  // Getters
  canAccessAccount: (accountId: number) => {
    const { user } = get()
    if (!user) return false
    return user.accessible_accounts?.includes(accountId) || false
  },

  getUserRole: () => {
    const { user } = get()
    return user?.role || 'user'
  },

  hasPermission: (moduleName: string) => {
    const { user, permissions } = get()

    if (!user) return false

    // Use registry predicate as single source of truth for "always accessible" modules.
    // This covers isCore:true AND requirePermission:false (e.g. allegro_tytuly_v2 BETA).
    if (isModulePublic(getModuleById(moduleName))) return true

    // Admins get all permissions
    if (user.role === 'admin') return true

    // Check explicit permissions
    return permissions[moduleName] === true
  },

  hasManualModuleGrant: (moduleName: string) => {
    const { user, permissions } = get()

    if (!user) return false
    if (user.role === 'admin') return true
    return permissions[moduleName] === true
  },

  loadUserPermissions: async (force = false) => {
    const { user, permissionsLoading, permissionsLastFetchedAt } = get()
    if (!user) return

    // Deduplicate concurrent calls — prevents React StrictMode double-invoke
    if (permissionsLoading) return

    if (!force && Date.now() - permissionsLastFetchedAt < PERMISSION_THROTTLE_MS) return

    set({ permissionsLoading: true })
    try {
      const response = await api.get('/auth/me/permissions')
      set({
        permissions: response.data.permissions || {},
        permissionsLastFetchedAt: Date.now(),
        permissionsLoading: false,
      })
    } catch (error) {
      console.error('Failed to load user permissions:', error)
      set({ permissions: {}, permissionsLoading: false })
    }
  },

  updatePermissions: (permissions: Record<string, boolean>) => {
    set({ permissions })
  },

  initializePermissions: async () => {
    const { user, isAuthenticated, loadUserPermissions } = get()
    if (user && isAuthenticated) {
      await loadUserPermissions()
    }
  },

  checkDocumentRequirements: () => {
    const { user, documentModalDismissed } = get()
    if (user?.requires_document_acceptance && !documentModalDismissed) {
      set({ showDocumentModal: true, showMarketingModal: false })
    } else {
      set({ showDocumentModal: false })
      // Only show marketing modal after legal docs are satisfied
      if (!user?.requires_document_acceptance && user?.requires_marketing_prompt) {
        set({ showMarketingModal: true })
      }
    }
  },

  dismissDocumentModal: () => {
    set({ showDocumentModal: false, documentModalDismissed: true })
  },

  documentAccepted: () => {
    const { user } = get()
    set({
      showDocumentModal: false,
      documentModalDismissed: false,
      user: {
        ...(user as User),
        requires_document_acceptance: false,
        missing_documents: [],
      },
    })
    // After legal docs are accepted, show marketing modal if not yet prompted
    if (user?.requires_marketing_prompt) {
      set({ showMarketingModal: true })
    }
  },

  dismissMarketingModal: () => {
    set({
      showMarketingModal: false,
      user: get().user ? { ...(get().user as User), requires_marketing_prompt: false } : null,
    })
  },

  marketingConsentSaved: (state: Record<string, boolean>) => {
    set({
      showMarketingModal: false,
      user: get().user
        ? {
            ...(get().user as User),
            requires_marketing_prompt: false,
            marketing_consent_state: state,
          }
        : null,
    })
  },
}))

// Wire the api layer to the auth store via DI — no circular imports, no event bus,
// the store remains the single source of truth for the in-memory access token.
initApiAuth({
  getAccessToken: () => useAuthStore.getState().token,
  setAccessToken: (accessToken, user) =>
    useAuthStore.getState().setAccessToken(accessToken, user as User | undefined),
  onLogout: () => useAuthStore.getState().logout(),
})
